<template>
  <el-dialog
    v-dialog-drag-resize
    :title="finalDialogTitle"
    :visible.sync="localDialogVisible"
    :width="dialogWidth"
    class="custom-form-dialog dialog-with-radius"
    :close-on-click-modal="closeOnClickModal"
    top="5vh"
    @close="handleDialogClose"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :label-width="labelWidth"
      :inline="inline"
      style="max-height: 700px; overflow-x: hidden; overflow-y: auto;"
      :label-position="labelPosition"
      :size="formSize"
      class="custom-form"
      :disabled="formDisabled"
      :validate-on-rule-change="validateOnRuleChange"
    >
      <el-row v-for="(row, rowIndex) in formConfig" :key="rowIndex" :gutter="gutter">
        <el-col v-for="(item, itemIndex) in row" :key="itemIndex" :span="item.span || 12" class="form-item-col">
          <template v-if="isItemShow(item)">
            <el-form-item
              :label="item.label"
              :prop="item.prop"
              :rules="item.rules || (item.required ? [{ required: true, message: `${item.label}不能为空`, trigger: 'blur' }] : [])"
              :disabled="getItemDisabledStatus(item)"
              :label-width="item.labelWidth || labelWidth"
              :label-position="item.labelPosition || labelPosition"
              :required="item.required"
              :error="item.error"
              :show-message="item.showMessage !== false"
              :inline-message="item.inlineMessage"
              :size="item.size || formSize"
            >
              <template v-if="item.type === 'custom'">
                <slot :name="`${item.prop}-slot`" :formData="formData" :dialogType="dialogType" :item="item"></slot>
              </template>
              <template v-else>
                <slot :name="`${item.prop}-slot`" :formData="formData" :dialogType="dialogType" :item="item">
                  <el-input
                    v-if="item.type === 'input'"
                    v-model="formData[item.prop]"
                    :type="item.inputType || 'text'"
                    :placeholder="item.placeholder || `请输入${item.label}`"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :maxlength="item.maxlength"
                    :minlength="item.minlength"
                    :show-word-limit="item.showWordLimit"
                    :clearable="item.clearable !== false"
                    :prefix-icon="item.prefixIcon"
                    :suffix-icon="item.suffixIcon"
                    :show-password="item.showPassword"
                    :readonly="item.readonly"
                    :autocomplete="item.autocomplete || 'off'"
                    @input="handleItemInput(item.prop, $event)"
                    @change="handleItemChange(item.prop, $event)"
                    @clear="handleItemClear(item.prop)"
                  />

                  <el-select
                    v-else-if="item.type === 'select'"
                    v-model="formData[item.prop]"
                    :placeholder="item.placeholder || `请选择${item.label}`"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :style="item.style"
                    :multiple="item.multiple"
                    :clearable="item.clearable !== false"
                    :filterable="item.filterable"
                    :remote="item.remote"
                    :remote-method="item.remoteMethod"
                    :loading="item.loading"
                    :collapse-tags="item.collapseTags"
                    :multiple-limit="item.multipleLimit"
                    :allow-create="item.allowCreate"
                    @change="handleItemChange(item.prop, $event)"
                    @clear="handleItemClear(item.prop)"
                    @blur="handleItemBlur(item.prop, $event)"
                  >
                    <el-option
                      v-for="opt in item.options"
                      :key="opt[getOptionValueKey(item)] || opt[getOptionLabelKey(item)]"
                      :label="opt[getOptionLabelKey(item)]"
                      :value="opt[getOptionValueKey(item)]"
                      :disabled="opt[getOptionDisabledKey(item)]"
                    />
                  </el-select>

                  <el-cascader
                    v-else-if="item.type === 'cascader'"
                    v-model="formData[item.prop]"
                    :options="item.options || []"
                    :props="item.props || {}"
                    :placeholder="item.placeholder || `请选择${item.label}`"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :clearable="item.clearable !== false"
                    :filterable="item.filterable"
                    :show-all-levels="item.showAllLevels !== false"
                    :collapse-tags="item.collapseTags"
                    :separator="item.separator"
                    @change="handleItemChange(item.prop, $event)"
                    @blur="handleItemBlur(item.prop, $event)"
                    @clear="handleItemClear(item.prop)"
                  />

                  <el-radio-group
                    v-else-if="item.type === 'radio'"
                    v-model="formData[item.prop]"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :text-color="item.textColor"
                    :fill="item.fill"
                    @change="handleItemChange(item.prop, $event)"
                  >
                    <el-radio
                      v-for="opt in item.options"
                      :key="opt[getOptionValueKey(item)] || opt[getOptionLabelKey(item)]"
                      :label="opt[getOptionValueKey(item)]"
                      :disabled="opt[getOptionDisabledKey(item)]"
                      :border="item.border"
                    >
                      {{ opt[getOptionLabelKey(item)] }}
                    </el-radio>
                  </el-radio-group>

                  <el-checkbox-group
                    v-else-if="item.type === 'checkbox'"
                    v-model="formData[item.prop]"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    @change="handleItemChange(item.prop, $event)"
                  >
                    <el-checkbox
                      v-for="opt in item.options"
                      :key="opt[getOptionValueKey(item)] || opt[getOptionLabelKey(item)]"
                      :label="opt[getOptionValueKey(item)]"
                      :disabled="opt[getOptionDisabledKey(item)]"
                      :border="item.border"
                    >
                      {{ opt[getOptionLabelKey(item)] }}
                    </el-checkbox>
                  </el-checkbox-group>

                  <el-date-picker
                    v-else-if="item.type === 'date'"
                    v-model="formData[item.prop]"
                    :type="item.dateType || 'date'"
                    :placeholder="item.placeholder || `请选择${item.label}`"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :format="item.format"
                    :value-format="item.valueFormat"
                    :range-separator="item.rangeSeparator || '至'"
                    :start-placeholder="item.startPlaceholder"
                    :end-placeholder="item.endPlaceholder"
                    :disabled-date="item.disabledDate"
                    :editable="item.editable !== false"
                    :clearable="item.clearable !== false"
                    :readonly="item.readonly"
                    :unlink-panels="item.unlinkPanels"
                    :prefix-icon="item.prefixIcon"
                    :clear-icon="item.clearIcon"
                    :validate-event="item.validateEvent !== false"
                    @change="handleItemChange(item.prop, $event)"
                    @blur="handleItemBlur(item.prop, $event)"
                    @clear="handleItemClear(item.prop)"
                  />

                  <el-time-picker
                    v-else-if="item.type === 'time'"
                    v-model="formData[item.prop]"
                    :placeholder="item.placeholder || `请选择${item.label}`"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :format="item.format || 'HH:mm:ss'"
                    :value-format="item.valueFormat"
                    :range-separator="item.rangeSeparator || '至'"
                    :start-placeholder="item.startPlaceholder"
                    :end-placeholder="item.endPlaceholder"
                    :disabled-hours="item.disabledHours"
                    :disabled-minutes="item.disabledMinutes"
                    :disabled-seconds="item.disabledSeconds"
                    :editable="item.editable !== false"
                    :clearable="item.clearable !== false"
                    :readonly="item.readonly"
                    :is-range="item.isRange"
                    :arrow-control="item.arrowControl"
                    @change="handleItemChange(item.prop, $event)"
                    @blur="handleItemBlur(item.prop, $event)"
                    @clear="handleItemClear(item.prop)"
                  />

                  <el-input-number
                    v-else-if="item.type === 'inputNumber'"
                    v-model="formData[item.prop]"
                    :min="item.min !== undefined ? item.min : -Infinity"
                    :max="item.max !== undefined ? item.max : Infinity"
                    :step="item.step !== undefined ? item.step : 1"
                    :precision="item.precision"
                    :placeholder="item.placeholder || `请输入${item.label}`"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :controls-position="item.controlsPosition || 'right'"
                    style="width: 100%"
                    @change="handleItemChange(item.prop, $event)"
                  />

                  <el-input
                    v-else-if="item.type === 'textarea'"
                    v-model="formData[item.prop]"
                    type="textarea"
                    :rows="item.rows || 4"
                    :placeholder="item.placeholder || `请输入${item.label}`"
                    :disabled="getItemDisabledStatus(item)"
                    :size="item.size || formSize"
                    :maxlength="item.maxlength"
                    :minlength="item.minlength"
                    :show-word-limit="item.showWordLimit"
                    :readonly="item.readonly"
                    :autosize="item.autosize"
                    @input="handleItemInput(item.prop, $event)"
                    @change="handleItemChange(item.prop, $event)"
                  />

                  <aj-upload
                    v-else-if="item.type === 'upload'"
                    :ref="`uploadRef_${item.prop}`"
                    :multiple="item.multiple !== false"
                    :accept-type="item.acceptType || ''"
                    :limit="item.limit || Infinity"
                    :history-files="formData[item.prop] ? formatUploadHistoryFiles(item, formData[item.prop]) : []"
                    :upload-btn-text="item.uploadBtnText || '选择文件'"
                    :show-limit-info="item.showLimitInfo !== false"
                    :allow-clear="item.allowClear !== false"
                    :max-file-size="item.maxFileSize || 0"
                    :upload-request="item.uploadRequest || null"
                    :upload-url="item.uploadUrl || ''"
                    :upload-headers="item.uploadHeaders || {}"
                    :upload-data="item.uploadData || {}"
                    :response-adapter="item.responseAdapter || null"
                    :disabled="getItemDisabledStatus(item)"
                    @upload-success="files => handleUploadSuccess(item, files)"
                    @upload-error="error => $emit('upload-error', { item, error })"
                  >
                    <template #upload-btn="{ trigger }">
                      <el-button type="primary" icon="el-icon-upload" :size="item.size || formSize" :disabled="getItemDisabledStatus(item)" @click="trigger">
                        {{ item.uploadBtnText || '选择文件' }}
                      </el-button>
                    </template>
                  </aj-upload>
                </slot>
              </template>
            </el-form-item>
          </template>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <slot name="footer" :confirm="handleConfirm" :cancel="handleCancel">
        <el-button :size="formSize" class="dialog-footer-btn cancel-btn" @click="handleCancel">
          {{ cancelText || '取消' }}
        </el-button>
        <el-button type="primary" :size="formSize" class="dialog-footer-btn confirm-btn" :loading="confirmLoading" @click="handleConfirm">
          {{ confirmText || (dialogType === 'add' ? '确认新增' : '确认编辑') }}
        </el-button>
      </slot>
    </template>
  </el-dialog>
</template>

<script>
import AjUpload from '../ajUpload/ajUpload.vue'

export default {
  name: 'BaseFormDialog',
  components: {
    AjUpload
  },
  props: {
    gutter: { type: Number, default: 20 },
    dialogType: {
      type: String,
      required: true,
      validator: val => ['add', 'edit'].includes(val)
    },
    formConfig: { type: Array, required: true },
    formData: { type: Object, required: true },
    dialogVisible: { type: Boolean, required: true },
    dialogTitle: { type: String, default: '' },
    dialogWidth: { type: [String, Number], default: '80%' },
    closeOnClickModal: { type: Boolean, default: false },
    labelWidth: { type: [String, Number], default: '80px' },
    labelPosition: {
      type: String,
      default: 'right',
      validator: val => ['left', 'right', 'top'].includes(val)
    },
    inline: { type: Boolean, default: false },
    formSize: {
      type: String,
      default: 'medium',
      validator: val => ['large', 'medium', 'small', 'mini'].includes(val)
    },
    formDisabled: { type: Boolean, default: false },
    cancelText: { type: String, default: '' },
    confirmText: { type: String, default: '' },
    validateOnRuleChange: { type: Boolean, default: true },
    optionLabelKey: { type: String, default: 'label' },
    optionValueKey: { type: String, default: 'value' },
    optionDisabledKey: { type: String, default: 'disabled' },
    resetUploadOnCancel: { type: Boolean, default: true },
    confirmLoading: { type: Boolean, default: false }
  },
  data () {
    return {
      localDialogVisible: this.dialogVisible
    }
  },
  computed: {
    finalDialogTitle () {
      if (this.dialogTitle) return this.dialogTitle
      return this.dialogType === 'add' ? '新增' : '编辑'
    }
  },
  watch: {
    dialogVisible (newVal) {
      this.localDialogVisible = newVal
    },
    localDialogVisible (newVal) {
      this.$emit('update:dialogVisible', newVal)
    }
  },
  methods: {
    getOptionLabelKey (item) {
      return item.optionLabelKey || this.optionLabelKey
    },
    getOptionValueKey (item) {
      return item.optionValueKey || this.optionValueKey
    },
    getOptionDisabledKey (item) {
      return item.optionDisabledKey || this.optionDisabledKey
    },
    isItemShow (item) {
      if (typeof item.show === 'function') return item.show(this.formData, this.dialogType)
      return item.show !== false
    },
    getItemDisabledStatus (item) {
      if (typeof item.disabled === 'function') return item.disabled(this.formData, this.dialogType)
      return this.formDisabled || item.disabled
    },
    handleItemInput (prop, value) {
      this.$emit('item-input', { prop, value, formData: this.formData })
    },
    handleItemChange (prop, value) {
      this.$emit('item-change', { prop, value, formData: this.formData })
    },
    handleItemClear (prop) {
      this.$emit('item-clear', { prop, formData: this.formData })
    },
    handleItemBlur (prop, event) {
      this.$emit('item-blur', { prop, event, formData: this.formData })
    },
    formatUploadHistoryFiles (item, value) {
      if (!value) return []
      if (Array.isArray(value)) {
        return value.map(file => this.normalizeUploadValue(item, file))
      }
      return [this.normalizeUploadValue(item, value)]
    },
    normalizeUploadValue (item, value) {
      if (typeof value === 'string') {
        return {
          name: value.split('/').pop() || '已上传文件',
          url: value,
          extension: value.split('.').pop() || '',
          type: 'document'
        }
      }
      return {
        ...value,
        name: value.name || value.fileName || '已上传文件',
        url: value.url || value[item.uploadField && item.uploadField.name ? item.uploadField.name : 'url'] || '',
        extension: value.extension || ((value.name || value.fileName || '').split('.').pop() || ''),
        type: value.type || 'document'
      }
    },
    handleUploadSuccess (item, files) {
      if (item.uploadField && item.uploadField.name) {
        this.$set(this.formData, item.prop, files.map(file => ({
          ...file,
          [item.uploadField.name]: file[item.uploadField.name] || file.url
        })))
      } else {
        this.$set(this.formData, item.prop, files)
      }
      this.$emit('item-change', { prop: item.prop, value: this.formData[item.prop], formData: this.formData })
    },
    resetAllUploadComponents () {
      if (!this.resetUploadOnCancel) return
      this.formConfig.forEach(row => {
        row.forEach(item => {
          if (item.type !== 'upload') return
          const refs = this.$refs[`uploadRef_${item.prop}`]
          const uploadComp = Array.isArray(refs) ? refs[0] : refs
          if (uploadComp && typeof uploadComp.resetAllState === 'function') {
            uploadComp.resetAllState()
          }
        })
      })
    },
    handleConfirm () {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.$emit('confirm', {
          data: this.formData,
          type: this.dialogType
        })
      })
    },
    handleCancel () {
      this.resetAllUploadComponents()
      this.localDialogVisible = false
      this.$emit('cancel', {
        data: this.formData,
        type: this.dialogType
      })
    },
    handleDialogClose () {
      this.$emit('update:dialogVisible', false)
    }
  }
}
</script>

<style scoped>
.custom-form-dialog ::v-deep .el-dialog__body {
  padding-top: 12px;
}

.form-item-col {
  margin-bottom: 4px;
}

.dialog-footer-btn {
  min-width: 96px;
}
</style>
